<template>
  <parent>
    <validation-observer
      ref="apiKeys"
      v-slot="{ invalid }"
      class="h-100"
      tag="form"
    >
      <form-wrapper
        v-if="rolesReady"
        full-width
        full-height
      >
        <template v-slot:body>
          <paper-simple class="h-100 m-auto">
            <div class="more-visible">
              <b-row class="justify-content-center">
                <b-col cols="12">
                  <p>
                    A role contains a set of permissions that allows you to
                    perform specific actions on Captain resources. To make
                    permissions available to users, you grant roles to the
                    members.
                  </p>
                  <ul>
                    <li class="mt-1">
                      <span class="text-thicker">Owner:</span>
                      The owner of the project with all editor permissions and
                      permissions for the following actions: Manage roles and
                      permissions for a project and all resources within the
                      project. Set up billing for a project.
                    </li>
                    <li class="mt-1">
                      <span class="text-thicker">Editor:</span>
                      All viewer permissions, plus permissions for actions that
                      modify state, such as changing existing resources.
                    </li>
                    <li class="mt-1">
                      <span class="text-thicker">Viewer:</span>
                      Permissions for read-only actions that do not affect the
                      state, such as viewing (but not modifying) existing
                      resources or data.
                    </li>
                  </ul>
                </b-col>
              </b-row>
              <b-row class="justify-content-center">
                <b-col
                  cols="12"
                  class="mt-2"
                >
                  <div class="full-form">
                    <b-row>
                      <b-col>
                        <validation-provider
                          #default="{ errors }"
                          name="Name"
                          rules="required"
                          class="w-100"
                        >
                          <input-field
                            name="api_key-name"
                            label="Name*"
                            label-position="top"
                            :error="errors.length ? errors[0] : null"
                          >
                            <b-form-input
                              id="api_key-name"
                              v-model="keyName"
                              placeholder="Enter API key name here..."
                              :state="errors.length > 0 ? false : null"
                            />
                          </input-field>
                        </validation-provider>
                      </b-col>
                    </b-row>
                    <b-row>
                      <b-col>
                        <validation-provider
                          #default="{ errors }"
                          name="Role"
                          rules="required"
                          class="w-100"
                        >
                          <input-field
                            name="api_key-role"
                            label="Role *"
                            label-position="top"
                            :error="errors.length ? errors[0] : null"
                          >
                            <v-select
                              id="api_key-role"
                              v-model="role"
                              label="label"
                              :state="errors.length > 0 ? false : null"
                              class="flex-fill distro-single-select"
                              :options="roleOptions"
                              placeholder="Select Role"
                            />
                          </input-field>
                        </validation-provider>
                      </b-col>
                    </b-row>
                    <b-row>
                      <b-col>
                        <custom-input-radio
                          v-model="scope"
                          label="Select scope"
                          :options="getObjectOptions(apiKey.apiKeyScopeOptions)"
                        />
                      </b-col>
                    </b-row>
                    <b-row>
                      <b-col>
                        <validation-provider
                          name="Valid until"
                          rules="required"
                          class="w-100"
                        >
                          <input-field
                            label="Valid Until *"
                            label-position="top"
                          >
                            <flat-pickr
                              v-model="validUntil"
                              placeholder="Select date & time"
                              class="form-control bg-white"
                              :config="{
                                enableTime: true,
                                minDate: new Date(),
                              }"
                            />
                          </input-field>
                        </validation-provider>
                      </b-col>
                    </b-row>
                  </div>
                </b-col>
              </b-row>
            </div>
            <span class="bg-icon">
              <GjIcon
                name="Api"
                size="140"
                class="img-fluid"
              />
            </span>
          </paper-simple>
        </template>
        <template v-slot:footer>
          <b-button
            variant="bg-white"
            @click="onCancel()"
          >
            Cancel
          </b-button>
          <button-loading
            variant="bg-primary"
            class="ml-1"
            :disabled="invalid"
            :spinning="updatingApiKey || creatingApiKey"
            @click="onSave()"
          >{{ !edit ? 'Generate' : 'Update' }} API Key</button-loading>
        </template>
      </form-wrapper>
      <loading-page
        v-else
        full
      />
    </validation-observer>
  </parent>
</template>

<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { required } from '@validations'
import CustomInputRadio from '@/components/form/CustomInputRadio.vue'
import flatPickr from 'vue-flatpickr-component'
import vSelect from 'vue-select'

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    CustomInputRadio,
    flatPickr,
    vSelect,
  },
  props: {
    editing: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      required,
      keyName: '',
      validUntil: null,
      role: null,
      roleOptions: [],
      rolesReady: false,
      edit: false,
      scope: 0,
    }
  },
  computed: {
    updatingApiKey() {
      return this.$store.state.apiKeys.updatingApiKey
    },
    creatingApiKey() {
      return this.$store.state.apiKeys.creatingApiKey
    },
    apiKey() {
      return this.$store.state.apiKeys.apiKey
    },
  },
  created() {
    if (this.editing) {
      this.keyName = this.apiKey.name
      this.validUntil = this.apiKey.validUntil
      this.edit = true
    }
    this.scope = this.apiKey?.scope
    this.$store
      .dispatch('roles/fetchRoles', {
        pageIndex: 0,
        pageSize: 500,
      })
      .then(() => {
        const role = this.$store.state.roles.roles
        for (let i = 0; i < role.length; i++) {
          this.roleOptions.push({ value: role[i].id, label: role[i].name })

          if (this.editing && role[i].id === this.apiKey.role) {
            this.role = { value: role[i].id, label: role[i].name }
          }
        }
        this.rolesReady = true
      })
  },
  methods: {
    onSave() {
      const apiKey = {
        name: this.keyName,
        role: this.role.value,
        validUntil: this.validUntil,
        scope: this.scope,
      }
      if (this.edit) {
        this.$store
          .dispatch('apiKeys/updateApiKey', {
            id: this.$route.params.id,
            body: apiKey,
          })
          .then(() => {
            this.$router.push({
              name: 'api-keys',
              params: {
                toast: {
                  title: 'Success',
                  variant: 'success',
                  message: 'API Key updated successfully!',
                },
              },
            })
          })
          .catch(error => {
            this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while updating API Key.')
          })
      } else {
        this.$store
          .dispatch('apiKeys/createApiKey', apiKey)
          .then(() => {
            this.$router.push({
              name: 'api-keys',
              params: {
                toast: {
                  title: 'Success',
                  variant: 'success',
                  message: 'API Key created successfully!',
                },
              },
            })
          })
          .catch(error => {
            this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while creating API Key.')
          })
      }
    },
    onCancel() {
      this.$router.push({ name: 'api-keys' })
    },
  },
}
</script>
<style lang="scss" scoped>
@import '@core/scss/vue/libs/vue-flatpicker.scss';
::v-deep {
  .paper {
    @media (min-width: 1200px) {
      width: 75%;
    }
  }
}
</style>
